import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Offer,
  updateOfferStatus,
  createCheckoutSession,
  claimFreeOffer,
  checkUserFreeOffer,
  getUserPurchasesByEmail
} from "../services/offreService";
import { toast } from "react-toastify";
import {
  FaCreditCard,
  FaEdit,
  FaTrash,
  FaToggleOn,
  FaToggleOff
} from "react-icons/fa";
import { MdEuroSymbol } from "react-icons/md";
import Swal from "sweetalert2";
import "../css/OfferCard.css";

export interface OfferWithSubscription extends Offer {
  isSubscribed?: boolean;
  stripe_subscription_id?: string;
}

type ColorKey = "lite" | "pro" | "advance" | "premium" | "business";

interface OfferCardsProps {
  offers: Offer[];
  setShowModalOffre: (show: boolean) => void;
  setSelectedOffer: (offer: Offer | undefined) => void;
  handleDelete: (id: number) => Promise<void>;
  userRole: "ADMIN" | "CLIENT";
}

export default function OfferCards({
  offers,
  setShowModalOffre,
  setSelectedOffer,
  handleDelete,
  userRole
}: OfferCardsProps) {
  const [localOffers, setLocalOffers] = useState<OfferWithSubscription[]>(offers);
  const [animate, setAnimate] = useState(false);
  const [hasFreeOffer, setHasFreeOffer] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<Record<number, 'monthly' | 'yearly'>>({});
  const [userSubscriptions, setUserSubscriptions] = useState<
    { offer_id: number; stripe_subscription_id: string }[]
  >([]);

  const [expandedDescriptions, setExpandedDescriptions] = useState<Record<number, boolean>>({});
  const [needsToggle, setNeedsToggle] = useState<Record<number, boolean>>({});
  const descriptionRefs = useRef<{ [key: number]: HTMLParagraphElement | null }>({});

  useEffect(() => {
    const newNeedsToggle: Record<number, boolean> = {};

    Object.keys(descriptionRefs.current).forEach(key => {
      const id = parseInt(key);
      const element = descriptionRefs.current[id];

      if (element) {
        const lineHeight = parseInt(window.getComputedStyle(element).lineHeight);
        const maxHeight = lineHeight * 3;

        newNeedsToggle[id] = element.scrollHeight > maxHeight;
      }
    });

    setNeedsToggle(newNeedsToggle);
  }, [localOffers]);

  const toggleDescription = (id: number) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    setAnimate(true);
    const filteredOffers = offers.filter(offre => {
      if (offre.monthly_price === 0 && hasFreeOffer && userRole === "CLIENT") {
        return false;
      }
      return true;
    });
    setLocalOffers(filteredOffers);
  }, [offers, hasFreeOffer, userRole]);

  useEffect(() => {
    const fetchFreeOfferStatus = async () => {
      if (userRole === "CLIENT") {
        const email = localStorage.getItem("userEmail");
        if (!email) return;

        try {
          const status = await checkUserFreeOffer(email);
          setHasFreeOffer(status.hasFreeOffer);
        } catch (error) {
          console.error("Erreur lors de la vérification de l'offre gratuite:", error);
        }
      }
    };

    fetchFreeOfferStatus();
  }, [userRole]);

  useEffect(() => {
    const fetchUserSubscriptions = async () => {
      const email = localStorage.getItem("userEmail");
      if (!email) return;

      try {
        const data = await getUserPurchasesByEmail(email);

        setLocalOffers(prev =>
          prev.map(offer => {
            const sub = data.purchases.find(p => p.offer.id === offer.id);
            if (sub && sub.stripe_subscription_id) {
              return {
                ...offer,
                isSubscribed: true,
                stripe_subscription_id: sub.stripe_subscription_id
              };
            }
            return offer;
          })
        );
      } catch (err) {
        console.error("Erreur récupération abonnements :", err);
      }
    };

    if (userRole === "CLIENT") fetchUserSubscriptions();
  }, [userRole]);

  const handleToggleStatus = async (id: number, currentStatus: "active" | "inactive") => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await updateOfferStatus(id, newStatus);
      setLocalOffers((prev) =>
        prev.map((offer) => (offer.id === id ? { ...offer, status: newStatus } : offer))
      );
      toast.success(`Offre ${newStatus === "active" ? "activée" : "désactivée"}`);
    } catch (error) {
      toast.error("Erreur lors de la mise à jour du statut");
    }
  };

  const handleBuyOffer = async (email: string, offer: Offer, period: 'monthly' | 'yearly') => {
    try {
      const result = await Swal.fire({
        title: `<div style="
    font-size: 24px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 5px;
  ">Confirmer votre abonnement</div>`,
        html: `
    <div style="
      background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
      border-radius: 12px;
      padding: 20px;
      margin: 15px 0;
      border-left: 4px solid #4bb0c0;
    ">
      <div style="
        display: flex;
        align-items: center;
        margin-bottom: 15px;
        padding-bottom: 15px;
        border-bottom: 1px solid #dee2e6;
      ">
        <div style="
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #4bb0c0, #3a9ca9);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 15px;
        ">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
            <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4V8l8 5 8-5v10zm-8-7L4 6h16l-8 5z"/>
          </svg>
        </div>
        <div>
          <div style="font-size: 18px; font-weight: 600; color: #2c3e50;">${offer.name}</div>
          <div style="font-size: 14px; color: #6c757d;">Abonnement ${period === 'monthly' ? 'mensuel' : 'annuel'}</div>
        </div>
      </div>
      
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px;">
        <div style="
          background: white;
          padding: 12px;
          border-radius: 8px;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        ">
          <div style="font-size: 12px; color: #6c757d; margin-bottom: 5px;">QUOTA JOURNALIER</div>
          <div style="font-size: 20px; font-weight: 700; color: #4bb0c0;">${offer.daily_emails?.toLocaleString() || 0}</div>
          <div style="font-size: 12px; color: #6c757d;">emails/jour</div>
        </div>
        
        <div style="
          background: white;
          padding: 12px;
          border-radius: 8px;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        ">
          <div style="font-size: 12px; color: #6c757d; margin-bottom: 5px;">QUOTA MENSUEL</div>
          <div style="font-size: 20px; font-weight: 700; color: #f4a261;">${offer.monthly_emails?.toLocaleString() || 0}</div>
          <div style="font-size: 12px; color: #6c757d;">emails/mois</div>
        </div>
        
        <div style="
          background: white;
          padding: 12px;
          border-radius: 8px;
          text-align: center;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        ">
          <div style="font-size: 12px; color: #6c757d; margin-bottom: 5px;">QUOTA ANNUEL</div>
          <div style="font-size: 20px; font-weight: 700; color: #9c27b0;">${offer.yearly_emails?.toLocaleString() || 0}</div>
          <div style="font-size: 12px; color: #6c757d;">emails/an</div>
        </div>
        
        <div style="
          background: linear-gradient(135deg, ${period === 'monthly' ? '#4bb0c0' : '#f4a261'}, ${period === 'monthly' ? '#3a9ca9' : '#ec7506'});
          padding: 12px;
          border-radius: 8px;
          text-align: center;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        ">
          <div style="font-size: 12px; color: white; margin-bottom: 5px;">PRIX</div>
          <div style="font-size: 28px; font-weight: 800; color: white; line-height: 1;">
            ${period === 'monthly' ? offer.monthly_price?.toFixed(2) : offer.yearly_price?.toFixed(2)} €
          </div>
          <div style="font-size: 12px; color: rgba(255,255,255,0.9);">
            /${period === 'monthly' ? 'mois' : 'an'}
          </div>
        </div>
      </div>
    </div>
    
    <div style="
      background: #f8f9fa;
      border-radius: 8px;
      padding: 15px;
      margin-top: 15px;
      font-size: 13px;
      color: #6c757d;
      border-left: 3px solid #ffc107;
    ">
      <div style="display: flex; align-items: center; margin-bottom: 5px;">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="#ffc107" style="margin-right: 8px;">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
        </svg>
        <strong>Information importante</strong>
      </div>
      Vous serez redirigé vers notre partenaire de paiement sécurisé pour finaliser votre abonnement.
    </div>
  `,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: `Souscrire ${period === 'monthly' ? 'mensuel' : 'annuel'}`,
        cancelButtonText: 'Revenir',
        confirmButtonColor: period === 'monthly' ? '#4bb0c0' : '#f4a261',
        cancelButtonColor: '#6c757d',
        showCloseButton: true,
        customClass: {
          popup: 'custom-swal-popup',
          title: 'custom-swal-title',
          confirmButton: 'custom-swal-confirm',
          cancelButton: 'custom-swal-cancel'
        },
        width: '600px',
        padding: '20px',
        background: '#ffffff'
      });
      if (!result.isConfirmed) return;

        const isFree =
          (period === 'monthly' ? offer.monthly_price : offer.yearly_price) === 0;

        if (isFree) {
          const data = await claimFreeOffer(email);
          toast.success(data.message);
          return;
        }

        const stripePriceId =
          period === 'monthly'
            ? offer.stripe_price_monthly
            : offer.stripe_price_yearly;

        if (!stripePriceId) {
          toast.error("Offre mal configurée (Stripe ID manquant)");
          return;
        }

        // ✅ 3. Paiement Stripe
        const data = await createCheckoutSession(email, offer.id, period);
        if (data.url) {
          window.location.href = data.url;
        } else {
          toast.error("Impossible de créer la session de paiement.");
        }


    } catch (error: any) {
      toast.error("Erreur lors de l'achat : " + error.message);
      console.error(error);
    }
  };

  const sortedOffers = useMemo(() => {
    return [...localOffers].sort((a, b) => {
      if (a.monthly_price === 0 && b.monthly_price > 0) return -1;
      if (b.monthly_price === 0 && a.monthly_price > 0) return 1;
      return (a.monthly_price || 0) - (b.monthly_price || 0);
    });
  }, [localOffers]);

  const colorKeys: ColorKey[] = ["lite", "pro", "advance", "premium", "business"];

  return (
    <div className="offcard-container">
      {userRole === "ADMIN" && (
        <div
          className="offcard-add-card"
          onClick={() => {
            setSelectedOffer(undefined);
            setShowModalOffre(true);
          }}
        >
          <div className="offcard-add-icon">+</div>
          <p className="offcard-add-text">Nouvelle offre</p>
        </div>
      )}

      {sortedOffers.map((offre, index) => {
        const hasLongDescription = needsToggle[offre.id];
        const isDescriptionExpanded = expandedDescriptions[offre.id];
        const isFree = offre.monthly_price === 0;

        return (
          <div
            key={offre.id}
            className={`offcard-card ${animate ? 'offcard-animate' : ''}`}
          >
            {/* Header avec effet pliable */}
            <div className="offcard-header">

              <div className="light-effect"></div>
              <div className="offcard-pin-shadow"></div>

              {/* Effet de pliage en coin */}

              <div className="offcard-title">
                <h3>{offre.name}</h3>

                {/* Indicateur de pliage (flèche) */}

              </div>


              {userRole === "ADMIN" && (
                <button
                  className="offcard-status-toggle"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleStatus(offre.id, offre.status || "active");
                  }}
                >
                  {offre.status === "active" ? (
                    <FaToggleOn size={40} color="#f4a261" />
                  ) : (
                    <FaToggleOff size={40} color="#ccc" />
                  )}
                </button>
              )}
            </div>
          {offre.description && (
            <div className="offcard-description-paper">
              <div className="offcard-description-content">
                <p
                  ref={el => {
                    descriptionRefs.current[offre.id] = el;
                  }}
                  className="offcard-description expanded"
                >
                  {offre.description}
                </p>
              </div>
            </div>
          )}
                      {/* Pricing Section */}
            <div className="offcard-pricing-section">
              {isFree ? (
                <div className="offcard-free-pricing">
                  <div className="offcard-price-main">Gratuit</div>
                </div>
              ) : (
                <>
                  <div className="offcard-price-options">
                    <div className="offcard-price-option">
                      <div className="offcard-price-label">Mensuel</div>
                      <div className="offcard-price-amount">
                        <MdEuroSymbol size={16} />
                        {offre.monthly_price?.toFixed(2)}
                        <span className="offcard-price-period">/ mois</span>
                      </div>
                    </div>

                    <div className="offcard-price-divider">|</div>

                    <div className="offcard-price-option">
                      <div className="offcard-price-label">Annuel</div>
                      <div className="offcard-price-amount offcard-highlight">
                        <MdEuroSymbol size={16} />
                        {offre.yearly_price?.toFixed(2)}
                        <span className="offcard-price-period">/ an</span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Quotas Section */}
            <div className="offcard-quotas-section">
              <div className="offcard-quota-row">
                <div className="offcard-quota-item">
                  <div className="offcard-quota-label">Par jour</div>
                  <div className="offcard-quota-value">{offre.daily_emails?.toLocaleString() || 0}</div>
                  <div className="offcard-quota-unit">emails</div>
                </div>
                <div className="offcard-quota-item">
                  <div className="offcard-quota-label">Par mois</div>
                  <div className="offcard-quota-value">{offre.monthly_emails?.toLocaleString() || 0}</div>
                  <div className="offcard-quota-unit">emails</div>
                </div>
                <div className="offcard-quota-item">
                  <div className="offcard-quota-label">Par an</div>
                  <div className="offcard-quota-value">{offre.yearly_emails?.toLocaleString() || 0}</div>
                  <div className="offcard-quota-unit">emails</div>
                </div>
              </div>
            </div>

            {/* Actions */}
            {userRole === "ADMIN" ? (
              <div className="offcard-admin-actions">
                <button
                  className="offcard-btn-edit"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOffer(offre);
                    setShowModalOffre(true);
                  }}
                >
                  <FaEdit size={14} />
                  Modifier
                </button>
                <button
                  className="offcard-btn-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    Swal.fire({
                      title: "Supprimer cette offre ?",
                      text: "Cette action est irréversible.",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#d33",
                      cancelButtonColor: "#4BB0C0",
                      confirmButtonText: "Supprimer",
                      cancelButtonText: "Annuler"
                    }).then(async (result) => {
                      if (result.isConfirmed) {
                        await handleDelete(offre.id);
                      }
                    });
                  }}
                >
                  <FaTrash size={14} />
                  Supprimer
                </button>
              </div>
            ) : (
              <div className="offcard-client-actions">
                {isFree ? (
                  <button
                    className="offcard-btn-free"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const email = localStorage.getItem("userEmail");
                      if (email) {
                        await handleBuyOffer(email, offre, 'monthly');
                      }
                    }}
                  >
                    <FaCreditCard size={16} />
                    Utiliser gratuitement
                  </button>
                ) : (
                  <div className="offcard-buy-options">
                    <button
                      className={`offcard-btn-monthly ${selectedPeriod[offre.id] === 'monthly' ? 'selected' : ''}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        setSelectedPeriod((prev) => ({ ...prev, [offre.id]: 'monthly' }));
                        const email = localStorage.getItem("userEmail");
                        if (email) await handleBuyOffer(email, offre, 'monthly');
                      }}
                    >
                      Mensuel
                    </button>

                    <button
                      className={`offcard-btn-yearly ${selectedPeriod[offre.id] === 'yearly' ? 'selected' : ''}`}
                      onClick={async (e) => {
                        e.stopPropagation();
                        setSelectedPeriod((prev) => ({ ...prev, [offre.id]: 'yearly' }));
                        const email = localStorage.getItem("userEmail");
                        if (email) await handleBuyOffer(email, offre, 'yearly');
                      }}
                    >
                      Annuel
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}